import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, mockResponse } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";

export interface SegmentCredibility {
  segmentId: string;
  label: string;
  level: "industry" | "office";
  matchedContracts: number;
  ownRate: number;
  tier: string;
  peers: string[];
  peerPooledRate: number;
  z: number;
  blendedRate: number;
}

export interface KGovernanceEntry {
  k: number;
  approvedBy: string;
  approvedAt: string;
  note: string;
}

export interface KGovernance {
  liveK: number;
  estimatedFrom: { segments: number; asOf: string };
  tierBoundaries: number[];
  log: KGovernanceEntry[];
}

/** Blend own experience with the tier pool: Z = n / (n + k). */
export function credibilityWeight(matchedContracts: number, k: number): number {
  if (k <= 0) return 1;
  return matchedContracts / (matchedContracts + k);
}

export function blendRate(ownRate: number, peerPooledRate: number, z: number): number {
  return Number((ownRate * z + peerPooledRate * (1 - z)).toFixed(2));
}

/** Recompute a row against a candidate k without touching the server. */
export function recalibrate(row: SegmentCredibility, k: number): SegmentCredibility {
  const z = credibilityWeight(row.matchedContracts, k);
  return {
    ...row,
    z: Number(z.toFixed(4)),
    blendedRate: blendRate(row.ownRate, row.peerPooledRate, z),
  };
}

export const calibrationKeys = {
  segments: (ticket?: TicketSize) => ["calibration", "segments", ticket ?? "all"] as const,
  kGovernance: () => ["calibration", "k-governance"] as const,
};

export function fetchCalibrationSegments(ticket?: TicketSize) {
  return apiGet<SegmentCredibility[]>("/calibration/segments", { ticket });
}

export function fetchKGovernance() {
  return apiGet<KGovernance>("/calibration/k-governance");
}

export function useCalibrationSegments() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: calibrationKeys.segments(ticket),
    queryFn: () => fetchCalibrationSegments(ticket),
    staleTime: 60_000,
  });
}

export function useKGovernance() {
  return useQuery({
    queryKey: calibrationKeys.kGovernance(),
    queryFn: fetchKGovernance,
    staleTime: 60_000,
  });
}

export function usePromoteK() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ k, note }: { k: number; note: string }) => {
      const entry: KGovernanceEntry = {
        k,
        approvedBy: "You · Pricing Governance",
        approvedAt: new Date().toISOString(),
        note,
      };
      return mockResponse(entry, 200);
    },
    onSuccess: (entry) => {
      queryClient.setQueryData<KGovernance>(calibrationKeys.kGovernance(), (previous) =>
        previous
          ? {
              ...previous,
              liveK: entry.k,
              estimatedFrom: { ...previous.estimatedFrom, asOf: entry.approvedAt.slice(0, 10) },
              log: [entry, ...previous.log],
            }
          : previous,
      );
      queryClient.setQueryData<SegmentCredibility[]>(calibrationKeys.segments(), (previous) =>
        previous
          ? [...previous.map((row) => recalibrate(row, entry.k))].sort((a, b) => a.z - b.z)
          : previous,
      );
    },
  });
}

/* ------------------------------------------------------------------ *
 * Loss development (chain ladder)
 * ------------------------------------------------------------------ */

export interface LossDevelopmentPoint {
  vintage: number;
  ageMonths: number;
  /** The vintage's own curve, present at EVERY age: the realized reading
   *  while the age is matured, the projection afterwards. */
  chartLossPct: number | null;
  /** True once the age is no longer realized. Switches that stretch of the
   *  curve from solid to dotted -- it is never dropped. */
  isProjected: boolean;
  /** The projection at every age regardless of maturity -- its own dotted
   *  line, for reading projection against actual as a backtest. */
  projectedLossPct: number | null;
}

export interface DevelopmentFactor {
  fromAge: number;
  toAge: number;
  factor: number;
}

export interface LossDevelopmentLedgerRow {
  vintage: number;
  industry: string;
  ageAtProjectionMonths: number;
  projectedUltimateLossPct: number;
  actualUltimateLossPct: number | null;
  errorPct: number | null;
}

export const DEVELOPMENT_AGES = [12, 24, 36, 48, 60] as const;
export const ULTIMATE_AGE = 60;

/** Same sentinel planning.ts uses -- selecting it sends no industry param,
 *  which the backend reads as "roll up across every sic_division". */
export const ALL_INDUSTRIES_LABEL = "All Industry";

// Industry options are fetched live via useIndustries() in ./reference --
// the old hardcoded list here ("Gas Pumps", "Car Washes", etc.) never
// matched any real sic_division value, so this dropdown returned empty data
// for every option, confirmed live.

export interface LossDevelopmentResponse {
  /** Null for "All Industry". */
  industry: string | null;
  points: LossDevelopmentPoint[];
  factors: DevelopmentFactor[];
}

/**
 * "realized" drops the backend's 2021+ floor entirely and returns every
 * fully-matured vintage instead (consider_60mo); omitted/"projected" is the
 * original unconditional 2021+ window, unchanged. See
 * risk-pricing-engine/app/repository/calibration.py:_vintage_filter.
 */
function basisParams(basis?: "realized" | "projected") {
  return basis === "realized" ? { basis } : undefined;
}

/**
 * Builds the query string for both endpoints below. `industries` is a real
 * array now (multi-select), sent as repeated `industry=` params -- FastAPI
 * binds repeated same-name params into a list on the backend, and this is
 * NOT something `apiGet`'s own `params` object can express (it only takes
 * one string per key), so the query string is built by hand here instead of
 * going through that convenience. Empty/undefined/"All Industry"-only omits
 * the param entirely, same as before.
 */
function buildQuery(
  industries: string[] | undefined,
  basis: "realized" | "projected" | undefined,
  ticket?: TicketSize,
): string {
  const params = new URLSearchParams();
  for (const industry of industries ?? []) {
    if (industry && industry !== ALL_INDUSTRIES_LABEL) params.append("industry", industry);
  }
  const basisParam = basisParams(basis);
  if (basisParam) params.set("basis", basisParam.basis);
  if (ticket) params.set("ticket", ticket);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function fetchLossDevelopment(industries?: string[], basis?: "realized" | "projected", ticket?: TicketSize) {
  return apiGet<LossDevelopmentResponse>(`/calibration/loss-development${buildQuery(industries, basis, ticket)}`);
}

export function fetchDevelopmentLedger(industries?: string[], basis?: "realized" | "projected", ticket?: TicketSize) {
  return apiGet<LossDevelopmentLedgerRow[]>(
    `/calibration/development-ledger${buildQuery(industries, basis, ticket)}`,
  );
}

/** `industries` empty/undefined means "All Industry" (the precomputed
 *  portfolio row) -- same sentinel-by-omission convention every other
 *  multi-value filter in this codebase uses. Query key sorts the selection so
 *  picking the same two industries in a different order doesn't miss the
 *  cache and refetch needlessly. */
export function useLossDevelopment(industries?: string[], basis?: "realized" | "projected") {
  const key = industries?.length ? [...industries].sort() : ALL_INDUSTRIES_LABEL;
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["calibration", "loss-development", key, basis ?? "projected", ticket ?? "all"] as const,
    queryFn: () => fetchLossDevelopment(industries, basis, ticket),
    staleTime: 60_000,
  });
}

export function useDevelopmentLedger(industries?: string[], basis?: "realized" | "projected") {
  const key = industries?.length ? [...industries].sort() : ALL_INDUSTRIES_LABEL;
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["calibration", "development-ledger", key, basis ?? "projected", ticket ?? "all"] as const,
    queryFn: () => fetchDevelopmentLedger(industries, basis, ticket),
    staleTime: 60_000,
  });
}
